import React, { useState, useEffect } from 'react';
import { Table, Button, Space, Tag, Spin, message, DatePicker, Modal } from 'antd';
import { Edit, Trash2 } from "lucide-react";
import ModalWrapper from '../../../../Modal';
import ApprovedOverTime from '../../../../modals/approvedOverTime';
import { getRequest, deleteRequest } from '../../../../service';
import dayjs from 'dayjs';
import { getEmployeeData } from '../../../../utils/localStorage';
import { formatDate } from '../../../../utils/dateTime';

const RequestOvertime = () => {

    const userData = getEmployeeData();
    const role = userData?.role;
    
    // Permission checks
    const overtimePermissions = userData?.designation?.permissions?.Attendance?.['Overtime Request'] || [];
    const canView = role === "companyadmin" || overtimePermissions.includes("view");
    const canEdit = role === "companyadmin" || overtimePermissions.includes("edit");
    const canDelete = role === "companyadmin" || overtimePermissions.includes("delete");

    const [editShiftModalOpen, setEditShiftModalOpen] = useState(false);
    const [selectedEntry, setSelectedEntry] = useState(null);
    const [overtimeRequests, setOvertimeRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedMonth, setSelectedMonth] = useState(dayjs());

    // Fetch overtime requests from API
    const fetchOvertimeRequests = async (month = selectedMonth) => {
        try {
            setLoading(true);
            if (!canView) {
                setOvertimeRequests([]);
                return;
            }
            
             const startDate = month.startOf('month').format('YYYY-MM-DD');
             const endDate = month.endOf('month').format('YYYY-MM-DD');
            let response;
            if(role === "companyadmin"){
                response = await getRequest(`/api/overtimerequest?startDate=${startDate}&endDate=${endDate}`);
            }else{
                response = await getRequest(`/api/overtimerequest?employeeId=${userData?.userId}&onlyMe=false&startDate=${startDate}&endDate=${endDate}`);
            }
           
            if (response.overtimeRequests) {
                // Transform API data to match table structure
                const transformedData = response.overtimeRequests.map((request, index) => ({
                    id: request._id,
                    employeeId: request.employeeId,
                    employeeName: `${request.employee?.firstName || ''} ${request.employee?.lastName || ''}`.trim(),
                    date: formatDate(request.attendanceLog?.date),
                    dateValue: new Date(request.attendanceLog?.date), // Keep original date for sorting
                    checkIn: request.attendanceLog?.checkIn || 'N/A',
                    checkOut: request.attendanceLog?.checkOut || 'N/A',
                    totalOverTime: request.totalOverTime || 'N/A',
                    paidOverTime: request.paidOverTime || 'N/A',
                    status: request.status || 'pending',
                    workingHour: request.attendanceLog?.workingHour || 'N/A',
                    approvedBy: request.approvedBy ? `${request.approvedBy.firstName || ''} ${request.approvedBy.lastName || ''}`.trim() : null,
                    originalData: request // Keep original data for reference
                }));
                
                // Sort by date in descending order (newest first)
                const sortedData = transformedData.sort((a, b) => {
                    if (!a.dateValue || !b.dateValue) return 0;
                    return b.dateValue.getTime() - a.dateValue.getTime();
                });
                
                setOvertimeRequests(sortedData);
            }
        } catch (error) {
            console.error('Error fetching overtime requests:', error);
            message.error('Failed to fetch overtime requests');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchOvertimeRequests();
    }, [canView]);

    const handleMonthChange = (date) => {
        setSelectedMonth(date);
        fetchOvertimeRequests(date);
    };

    const getStatusColor = (status) => {
        switch (status.toLowerCase()) {
            case 'approved':
                return 'success';
            case 'pending':
                return 'warning';
            case 'rejected':
                return 'error';
            default:
                return 'default';
        }
    };

    const handleEditClick = (record) => {
        if (!canEdit) {
            message.warning("You don't have permission to edit overtime requests.");
            return;
        }
        setSelectedEntry(record);
        setEditShiftModalOpen(true);
    };

    const handleCloseEditShiftModal = () => {
        setEditShiftModalOpen(false);
        setSelectedEntry(null);
    };

    const handleEditSuccess = () => {
        setEditShiftModalOpen(false);
        setSelectedEntry(null);
        // Refresh data after successful edit
        fetchOvertimeRequests();
    };

    const handleDelete = (record) => {
        if (!canDelete) {
            message.warning("You don't have permission to delete overtime requests.");
            return;
        }

        Modal.confirm({
            title: 'Are you sure you want to delete this overtime request?',
            content: `This will permanently delete the overtime request for ${record.employeeName} on ${record.date}.`,
            okText: 'Yes, Delete',
            okType: 'danger',
            cancelText: 'Cancel',
            onOk: async () => {
                try {
                    await deleteRequest(`/api/overtimerequest?id=${record.id}`);
                    message.success('Overtime request deleted successfully');
                    fetchOvertimeRequests(); // Refresh the data
                } catch (error) {
                    console.error('Error deleting overtime request:', error);
                    message.error('Failed to delete overtime request');
                }
            },
        });
    };

    const columns = [
        {
            title: 'Employee',
            dataIndex: 'employeeName',
            key: 'employeeName',
            width: 150,
        },
        {
            title: 'Date',
            dataIndex: 'date',
            key: 'date',
            width: 120,
        },
        {
            title: 'Check In',
            dataIndex: 'checkIn',
            key: 'checkIn',
            width: 100,
        },
        {
            title: 'Check Out',
            dataIndex: 'checkOut',
            key: 'checkOut',
            width: 100,
        },
        {
            title: 'Working Hours',
            dataIndex: 'workingHour',
            key: 'workingHour',
            width: 120,
        },
        {
            title: 'Total Overtime',
            dataIndex: 'totalOverTime',
            key: 'totalOverTime',
            width: 120,
        },
        {
            title: 'Paid Overtime',
            dataIndex: 'paidOverTime',
            key: 'paidOverTime',
            width: 120,
        },
        {
            title: 'Approved By',
            dataIndex: 'approvedBy',
            key: 'approvedBy',
            width: 120,
            render: (approvedBy) => approvedBy || 'N/A',
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            width: 100,
            render: (status) => (
                <Tag color={getStatusColor(status)}>
                    {status}
                </Tag>
            ),
        },
        {
            title: 'Actions',
            key: 'actions',
            width: 100,
            fixed: 'right',
            render: (_, record) => {
                const isApproved = record.status?.toLowerCase() === 'approved';
                const canEditRecord = canEdit && !isApproved;
                const canDeleteRecord = canDelete && !isApproved;
                
                return (
                    <Space>
                        <Button
                            type="text"
                            icon={<Edit className="w-4 h-4" />}
                            onClick={() => handleEditClick(record)}
                            className={canEditRecord ? "text-blue-600 hover:text-blue-800" : "text-gray-400 cursor-not-allowed"}
                            disabled={!canEditRecord}
                            title={!canEdit ? "You don't have permission to edit" : isApproved ? "Cannot edit approved requests" : "Edit"}
                        />
                        <Button
                            type="text"
                            icon={<Trash2 className="w-4 h-4" />}
                            disabled={!canDeleteRecord}
                            onClick={() => handleDelete(record)}
                            className={canDeleteRecord ? "text-red-600 hover:text-red-800" : "text-gray-400 cursor-not-allowed"}
                            title={!canDelete ? "You don't have permission to delete" : isApproved ? "Cannot delete approved requests" : "Delete"}
                        />
                    </Space>
                );
            },
        },
    ];

    return (
        <>
            <div className="p-4">
                <div className="flex justify-between items-center mb-4">
                        <h1 className="text-2xl font-semibold">Overtime Requests </h1>

                        <DatePicker
                            picker="month"
                            value={selectedMonth}
                            onChange={handleMonthChange}
                            format="MMMM YYYY"
                            className="w-40"
                        />
                </div>

                <Table
                    columns={columns}
                    dataSource={canView ? overtimeRequests : []}
                    rowKey="id"
                    scroll={{ x: 1500 }}
                    loading={loading}
                    pagination={{
                        total: canView ? overtimeRequests.length : 0,
                        pageSize: 10,
                        showSizeChanger: true,
                        showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
                    }}
                />
                {!canView && (
                    <div className="text-center text-gray-500 py-4">You don't have permission to view overtime requests.</div>
                )}
            </div>
            <ModalWrapper
                open={editShiftModalOpen}
                onRequestClose={handleCloseEditShiftModal}
                title="Overtime Request Details"
            >
                <ApprovedOverTime
                    onClose={handleCloseEditShiftModal}
                    onSuccess={handleEditSuccess}
                    initialData={selectedEntry || {}}
                />
            </ModalWrapper>
        </>
    );
};

export default RequestOvertime;
